import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid, GridProps } from "@material-ui/core";

// Utility
import Utility from "../../domain/classes/Utility";

// Components
import StyledPromptedTextField from "../common/PromptedTextField.Component";
import FormattedFieldSet from "../common/FormattedFieldSet.Component";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import StyledPromptedSelect from "../common/PromptedSelect.Component";
import ISelectable from "../../domain/interfaces/ISelectable";
import Transformer from "../../domain/classes/Transformer";
import Patient from "../../domain/classes/Patient";

export const styles = (theme: Theme) => createStyles({ root: {} });

export interface PrimaryInfoFieldsContainerProps extends GridProps {}

const PrimaryInfoFieldsContainer = React.forwardRef<
	HTMLDivElement,
	PrimaryInfoFieldsContainerProps & WithStyles<typeof styles>
>(({ classes, className, ...gridProps }, ref) => {
	// Contexts
	const {
		handlePatientDetailsChange,
		patientValidationError,
		clearPatientValidationError,
		patientDetails,
	} = useApplicationManager();

	// States
	const [listOfSexes, setListOfSexes] = React.useState<ISelectable[]>([
		{ key: "none", displayText: "Please select", value: "none" },
		...Transformer.arrayToISelectable(Patient.LIST_OF_SEXES),
	]);

	const handleGivenNameChange = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handlePatientDetailsChange("givenName", event.target.value);
	};

	const handleMiddleNameChange = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handlePatientDetailsChange("middleName", event.target.value);
	};

	const handleFamilyNameChange = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handlePatientDetailsChange("familyName", event.target.value);
	};

	const handleSexChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		handlePatientDetailsChange("sex", event.target.value as string);
	};

	return (
		<Grid
			container
			spacing={1}
			ref={ref}
			{...gridProps}
			className={clsx(classes.root, className)}
		>
			<Grid item xs={12}>
				<FormattedFieldSet label="Primary information">
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<StyledPromptedTextField
								title="Legal given name"
								fullWidth
								variant="outlined"
								label="Given name"
								size="small"
								onBlur={handleGivenNameChange}
								error={!!patientValidationError.givenNameError}
								helperText={
									patientValidationError.givenNameError
								}
								onFocus={() =>
									clearPatientValidationError(
										"givenNameError"
									)
								}
								defaultValue={patientDetails.givenName}
								autoFocus
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<StyledPromptedTextField
								title="Legal middle name"
								fullWidth
								variant="outlined"
								label="Middle name"
								size="small"
								onBlur={handleMiddleNameChange}
								defaultValue={patientDetails.middleName}
							/>
						</Grid>
						<Grid item xs={12}>
							<StyledPromptedTextField
								title="Legal family name"
								fullWidth
								variant="outlined"
								label="Family name"
								size="small"
								onBlur={handleFamilyNameChange}
								error={!!patientValidationError.familyNameError}
								helperText={
									patientValidationError.familyNameError
								}
								onFocus={() =>
									clearPatientValidationError(
										"familyNameError"
									)
								}
								defaultValue={patientDetails.familyName}
							/>
						</Grid>
						<Grid item xs={12}>
							<StyledPromptedSelect
								title="Sex"
								selectLabel="Sex"
								options={listOfSexes}
								onChange={handleSexChange}
								errorText={patientValidationError.sexError}
								onFocus={() =>
									clearPatientValidationError("sexError")
								}
							/>
						</Grid>
					</Grid>
				</FormattedFieldSet>
			</Grid>
		</Grid>
	);
});

const StyledPrimaryInfoFieldsContainer = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("PrimaryInfoFieldsContainer"),
})(PrimaryInfoFieldsContainer);

export default StyledPrimaryInfoFieldsContainer;

export const AnimatedPrimaryInfoFieldsContainer = animated(
	StyledPrimaryInfoFieldsContainer
);
